import { Brand } from '@retail/auth/types';
import { BrandCampaignDTO, CampaignStatus, MemberCampaignDTO } from '../dto';
import { CampaignUser } from './CampaignUser';
import { BaseCampaignItem } from './BaseCampaignItem';
import { MemberCampaignItem } from './MemberCampaignItem';
import { BrandCampaignItem } from './BrandCampaignItem';

export const centralCampaignTypes = ['CENTRAL', 'OPTIONAL', 'SUPPLIER'] as const;
export const localCampaignTypes = ['LOCAL'] as const;
export const campaignTypes = [...centralCampaignTypes, ...localCampaignTypes] as const;

export type CentralCampaignType = (typeof centralCampaignTypes)[number];
export type LocalCampaignType = (typeof localCampaignTypes)[number];
export type CampaignType = CentralCampaignType | LocalCampaignType;

export type CampaignAction = 'EDIT' | 'DELETE';

export interface CampaignBaseProps {
  id: string;
  campaignName: string;
  campaignCode: number;
  campaignType: CampaignType;
  mandatory: boolean;
  description?: string;
  validFrom: Date;
  validTo: Date;
  purchasePeriodFrom: Date;
  purchasePeriodTo: Date;
  releaseStatus: CampaignStatus;
  lastUpdated?: Date;
  createdBy?: CampaignUser;
  hasNewUpdates?: boolean;
  itemCount: number;
  actions: CampaignAction[];
}

type EditableField =
  | 'name'
  | 'description'
  | 'campaignPeriod'
  | 'purchasePeriod'
  | 'campaignType'
  | 'mandatory'
  | 'brands';

export type EditableItemField = 'campaignUnit' | 'salesPrice' | 'purchasePrice' | 'purchasePeriod';

export abstract class CampaignBase<TItem extends BaseCampaignItem> {
  id: string;
  name: string;
  campaignCode: number;
  campaignType: CampaignType;
  mandatory: boolean;
  description?: string;
  validFrom: Date;
  validTo: Date;
  purchasePeriodFrom: Date;
  purchasePeriodTo: Date;
  releaseStatus: CampaignStatus;
  lastUpdated?: Date;
  createdBy?: CampaignUser;
  hasNewUpdates?: boolean;
  itemCount: number;
  protected actions: CampaignAction[];

  protected constructor({
    id,
    campaignName,
    campaignCode,
    campaignType,
    mandatory,
    description,
    validFrom,
    validTo,
    purchasePeriodFrom,
    purchasePeriodTo,
    releaseStatus,
    lastUpdated,
    createdBy,
    hasNewUpdates,
    itemCount,
    actions,
  }: CampaignBaseProps) {
    this.id = id;
    this.name = campaignName;
    this.campaignCode = campaignCode;
    this.campaignType = campaignType;
    this.mandatory = mandatory;
    this.description = description;
    this.validFrom = validFrom;
    this.validTo = validTo;
    this.purchasePeriodFrom = purchasePeriodFrom;
    this.purchasePeriodTo = purchasePeriodTo;
    this.releaseStatus = releaseStatus;
    this.lastUpdated = lastUpdated;
    this.createdBy = createdBy;
    this.hasNewUpdates = hasNewUpdates;
    this.itemCount = itemCount;
    this.actions = actions;
    this.itemCount = itemCount;
  }

  hasGeneralEditPermissions = () => this.actions.includes('EDIT');

  abstract isCampaignEditable: () => boolean;

  abstract editableFields: () => EditableField[];

  abstract editableItemFields: (item: TItem) => EditableItemField[];

  isFieldEditable = (field: EditableField): boolean => this.editableFields().includes(field);

  hasGeneralDeletePermissions = () => this.actions.includes('DELETE');

  abstract isCampaignDeletable: () => boolean;
}

export interface MemberCampaignProps extends CampaignBaseProps {
  enrolled: boolean;
}

export class MemberCampaign extends CampaignBase<MemberCampaignItem> {
  enrolled: boolean;

  constructor(props: MemberCampaignProps) {
    super(props);
    this.enrolled = props.enrolled;
  }

  static fromDto = ({
    dto,
    canEdit,
    canDelete,
  }: {
    dto: MemberCampaignDTO;
    canEdit: boolean;
    canDelete: boolean;
  }): MemberCampaign => {
    const actions: CampaignAction[] = [];
    if (canEdit) actions.push('EDIT');
    if (canDelete) actions.push('DELETE');

    return new MemberCampaign({
      id: dto.id,
      campaignName: dto.campaignName,
      campaignCode: dto.campaignCode,
      mandatory: dto.mandatory,
      description: dto.description,
      validFrom: new Date(dto.validFrom),
      validTo: new Date(dto.validTo),
      purchasePeriodFrom: new Date(dto.purchasePeriodFrom),
      purchasePeriodTo: new Date(dto.purchasePeriodTo),
      releaseStatus: dto.releaseStatus,
      lastUpdated: dto.lastUpdated ? new Date(dto.lastUpdated) : undefined,
      createdBy: dto.createdBy ? CampaignUser.fromDto(dto.createdBy) : undefined,
      hasNewUpdates: dto.hasNewUpdates,
      itemCount: dto.itemCount,
      campaignType: dto.campaignType,
      enrolled: dto.isEnrolled,
      actions,
    });
  };

  isCampaignEditable = () => this.hasGeneralEditPermissions() && this.campaignType === 'LOCAL';

  editableFields = () => {
    if (this.hasGeneralEditPermissions() && this.campaignType === 'LOCAL') {
      return ['name', 'description', 'campaignPeriod', 'purchasePeriod'] satisfies EditableField[];
    }

    return [];
  };

  editableItemFields = (item: MemberCampaignItem) => {
    if (!this.hasGeneralEditPermissions()) return [];
    if (this.campaignType === 'LOCAL') {
      return [
        'campaignUnit',
        'salesPrice',
        'purchasePrice',
        'purchasePeriod',
      ] satisfies EditableItemField[];
    }
    if (!this.enrolled) return [];
    return (item.isSalesPriceOverridable ? ['salesPrice'] : []) satisfies EditableItemField[];
  };

  isCampaignDeletable = () => this.hasGeneralDeletePermissions() && this.campaignType === 'LOCAL';

  static fromDtoList = ({
    dtos,
    canEdit,
    canDelete,
  }: {
    dtos: MemberCampaignDTO[];
    canEdit: (dto: MemberCampaignDTO) => boolean;
    canDelete: (dto: MemberCampaignDTO) => boolean;
  }): MemberCampaign[] => {
    return dtos.map((dto) =>
      MemberCampaign.fromDto({ dto, canEdit: canEdit(dto), canDelete: canDelete(dto) })
    );
  };
}

export interface BrandCampaignProps extends CampaignBaseProps {
  brands: Brand[];
  updatedBy?: CampaignUser;
}

export class BrandCampaign extends CampaignBase<BrandCampaignItem> {
  brands: Brand[];
  updatedBy?: CampaignUser;

  constructor(props: BrandCampaignProps) {
    super(props);
    this.brands = props.brands;
    this.campaignType = props.campaignType;
    this.updatedBy = props.updatedBy;
  }

  isCampaignEditable = () => this.hasGeneralEditPermissions();

  editableFields = () => {
    if (!this.hasGeneralEditPermissions()) return [];

    if (this.releaseStatus === 'UNRELEASED') {
      return [
        'name',
        'description',
        'campaignPeriod',
        'purchasePeriod',
        'campaignType',
        'mandatory',
        'brands',
      ] satisfies EditableField[];
    }

    return ['name', 'description', 'purchasePeriod'] satisfies EditableField[];
  };

  editableItemFields = () => {
    if (!this.hasGeneralEditPermissions()) return [];

    return [
      'campaignUnit',
      'salesPrice',
      'purchasePrice',
      'purchasePeriod',
    ] satisfies EditableItemField[];
  };

  isCampaignDeletable = () => this.hasGeneralDeletePermissions();

  static fromDto = ({
    dto,
    canEdit,
    canDelete,
  }: {
    dto: BrandCampaignDTO;
    canEdit: boolean;
    canDelete: boolean;
  }): BrandCampaign => {
    const actions: CampaignAction[] = [];
    if (canEdit) actions.push('EDIT');
    if (canDelete) actions.push('DELETE');
    return new BrandCampaign({
      id: dto.id,
      campaignName: dto.campaignName,
      campaignCode: dto.campaignCode,
      mandatory: dto.mandatory,
      description: dto.description,
      validFrom: new Date(dto.validFrom),
      validTo: new Date(dto.validTo),
      purchasePeriodFrom: new Date(dto.purchasePeriodFrom),
      purchasePeriodTo: new Date(dto.purchasePeriodTo),
      lastUpdated: dto.lastUpdated ? new Date(dto.lastUpdated) : undefined,
      createdBy: dto.createdBy ? CampaignUser.fromDto(dto.createdBy) : undefined,
      hasNewUpdates: dto.hasNewUpdates,
      itemCount: dto.itemCount,
      brands: dto.brands,
      campaignType: dto.campaignType,
      releaseStatus: dto.releaseStatus,
      updatedBy: dto.updatedBy ? CampaignUser.fromDto(dto.updatedBy) : undefined,
      actions,
    });
  };

  static fromDtoList = ({
    dtos,
    canEdit,
    canDelete,
  }: {
    dtos: BrandCampaignDTO[];
    canEdit: boolean;
    canDelete: boolean;
  }): BrandCampaign[] => {
    return dtos.map((dto) => BrandCampaign.fromDto({ dto, canEdit, canDelete }));
  };
}
